import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import type { TaskCard as TaskCardData } from '../../services/api/board';
import { SlaTaskCard } from '../projects/SlaTaskCard';
import { SLA_TASK_TYPE_ID } from '../../constants/typeIds';
import { Avatar } from '../ui/Avatar';
import { MtPriorityBadge } from '../mytasks/MtPriorityBadge';
import { TaskLabelChips } from './TaskLabelChips';
import { useInternalChipVisibility } from './internalChipVisibility';

export interface TaskCardDragOverlayProps {
  task: TaskCardData;
  /**
   * Phase 41 Plan 41-08 (Surface 5d-A) — parent project's `taskTypeId`.
   * Forwarded from KanbanBoardPage so the drag-overlay ghost matches the
   * source card visually (SLA bars stay attached during the drag, not just
   * on the static row). Per checker I-09 this is an explicit prop, never
   * read from `task.project.taskTypeId` (that field does not exist on
   * TaskCardData).
   */
  projectTaskTypeId?: number | null;
}

/**
 * Phase 24 D-07.3 — DragOverlay ghost. Static div, no listeners, no
 * droppable. Width locked to w-72 to match column card width exactly.
 *
 * Phase 41 Plan 41-08 — when the parent project is on the SLA Task
 * template (Surface 5d-A), the ghost mirrors the source card's inline
 * dual-bar timer so the visual stays consistent during the drag.
 */
export function TaskCardDragOverlay({ task, projectTaskTypeId }: TaskCardDragOverlayProps) {
  // MKS-534 — the ghost mirrors the source card, so it drops the "Internal"
  // chip on exactly the boards TaskCard drops it on (same provider, mounted
  // above both).
  const { isInternalChipHidden } = useInternalChipVisibility();
  const visibilityChipHidden = isInternalChipHidden(task.projectId) && !task.isExternal;
  // Phase 41 Plan 41-17 (Task 1) — v7 token recolor (D-Kanban-recolor):
  // border-gray-200 → border-ms-border, bg-white → bg-ms-surface,
  // text-gray-900 → text-ms-text-strong, border-gray-100 → border-ms-border-subtle.
  // Amber/green chip retained per D-Tokens (Tailwind semantic palette stays).
  return (
    <div className="rounded-lg border border-ms-border bg-ms-surface p-3 shadow-xl opacity-90 rotate-[1.5deg] cursor-grabbing w-72">
      <div className="flex items-start justify-between gap-2">
        <h4 className="text-sm font-medium text-ms-text-strong line-clamp-2 flex-1 min-w-0">{task.name}</h4>
        {/* Phase 66 (EAP-07) — right cluster mirrors TaskCard: assignee avatar +
            optional external-assignee indicator + task visibility badge.
            Prior to Phase 66 the drag overlay rendered no assignee at all.
            MKS-163 follow-up — second row (priority badge) added below to
            stay consistent with TaskCard.tsx's right-side stack. */}
        <div className="flex flex-col items-end gap-1 shrink-0">
          <div className="flex items-center gap-1.5 shrink-0">
            {task.assigneeName && (
              <Avatar
                name={task.assigneeName}
                colorSeq={task.assigneeColorSeq}
                avatarUrl={task.assigneeAvatarUrl}
                size="xs"
              />
            )}
            {task.assigneeName && task.assigneeKind === 'external_user' && (
              <span className="text-[9px] font-semibold px-1 py-0.5 rounded-full bg-amber-50 text-amber-700 shrink-0">
                External
              </span>
            )}
            {!visibilityChipHidden && (
              <span
                className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium shrink-0 ${
                  task.isExternal ? 'bg-amber-50 text-amber-600' : 'bg-green-50 text-green-600'
                }`}
              >
                {task.isExternal ? (
                  <EyeSlashIcon className="w-4 h-4" />
                ) : (
                  <EyeIcon className="w-4 h-4" />
                )}
                {task.isExternal ? 'External' : 'Internal'}
              </span>
            )}
          </div>
          <MtPriorityBadge priority={task.priority ?? 'normal'} withLabel />
        </div>
      </div>
      {projectTaskTypeId === SLA_TASK_TYPE_ID && (
        <div className="mt-2 pt-2 border-t border-ms-border-subtle">
          <SlaTaskCard taskId={task.id} enabled={true} density="inline" />
        </div>
      )}
      {/* MKS-164 — identical markup to TaskCard's chip row; the drag ghost
          must stay visually consistent with the source card (an already
          established precedent on this file). */}
      <TaskLabelChips labels={task.labels} />
    </div>
  );
}
